import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Download, Pencil, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { inr, fmtDate, downloadCSV } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/lib/settings";
import { periodPresets, todayISO } from "@/lib/period";
import { PageHeader, Empty, KPI, PeriodPicker, Section } from "@/components/Bits";
import Modal from "@/components/Modal";

const blank = (s) => ({
  date: todayISO(), category: s?.expense_categories?.[0] || "", description: "", payee: "",
  amount: "", gst_rate: 0, method: s?.payment_methods?.[0] || "Cash", reference: "", notes: "",
});

export function ExpenseForm({ open, onClose, initial, onSaved }) {
  const s = useSettings();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setF(initial ? { ...initial } : blank(s)); }, [open, initial, s]);
  if (!open || !f || !s) return null;

  const set = (k, v) => setF({ ...f, [k]: v });
  const amount = Number(f.amount) || 0;
  const gst = (amount * Number(f.gst_rate || 0)) / 100;

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, amount, gst_rate: Number(f.gst_rate || 0) };
      const r = f.id ? await api.put(`/expenses/${f.id}`, body) : await api.post("/expenses", body);
      toast.success(f.id ? "Expense updated" : `Expense ${r.data.number} recorded`);
      onSaved?.(r.data);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={f.id ? `Edit ${f.number}` : "Record Expense"} testid="expense-form">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="label">Date</label><input type="date" className="field" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="exp-date" /></div>
        <div>
          <label className="label">Category</label>
          <select className="field" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="exp-category">
            {[...new Set([...(s.expense_categories || []), f.category].filter(Boolean))].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="col-span-2"><label className="label">Description</label><input className="field" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. September shop rent" data-testid="exp-description" /></div>
        <div><label className="label">Paid to</label><input className="field" value={f.payee} onChange={(e) => set("payee", e.target.value)} placeholder="Person or company" data-testid="exp-payee" /></div>
        <div><label className="label">Bill / receipt no.</label><input className="field font-num" value={f.reference} onChange={(e) => set("reference", e.target.value)} data-testid="exp-reference" /></div>
        <div><label className="label">Amount (before GST)</label><input type="number" min="0" step="0.01" className="field font-num" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="exp-amount" /></div>
        <div>
          <label className="label">GST on bill</label>
          <select className="field" value={f.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} data-testid="exp-gst">
            {[...new Set([0, ...(s.tax_rates || []), 28])].sort((a, b) => a - b).map((r) => <option key={r} value={r}>{r === 0 ? "No GST" : `${r}%`}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Paid by</label>
          <select className="field" value={f.method} onChange={(e) => set("method", e.target.value)} data-testid="exp-method">
            {[...new Set([...(s.payment_methods || []), f.method].filter(Boolean))].map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div className="text-right self-end pb-1">
          <div className="text-xs text-[#8C929B]">GST {inr(gst)}</div>
          <div className="font-num text-lg font-semibold">{inr(amount + gst)}</div>
        </div>
        <div className="col-span-2"><label className="label">Notes</label><input className="field" value={f.notes} onChange={(e) => set("notes", e.target.value)} /></div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy || !f.category || amount <= 0} data-testid="exp-save">{f.id ? "Save changes" : "Record expense"}</button>
      </div>
    </Modal>
  );
}

export default function Expenses() {
  const { isOwner } = useAuth();
  const s = useSettings();
  const presets = useMemo(() => periodPresets(s?.fy_start_month || 4), [s?.fy_start_month]);
  const [period, setPeriod] = useState(() => { const p = periodPresets(4)[1]; return { start: p.start, end: p.end }; });
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ open: false, initial: null });
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("new")) { setForm({ open: true, initial: null }); setParams({}, { replace: true }); }
  }, [params, setParams]);

  const load = async () => {
    const params = new URLSearchParams();
    if (period.start) params.set("start", period.start);
    if (period.end) params.set("end", period.end);
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    setList((await api.get(`/expenses?${params}`)).data);
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [period, category, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (e) => {
    if (!window.confirm(`Delete ${e.number} (${inr(e.total)})? This can't be undone.`)) return;
    try { await api.delete(`/expenses/${e.id}`); toast.success("Expense deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const total = list.reduce((a, e) => a + e.total, 0);
  const base = list.reduce((a, e) => a + e.amount, 0);
  const gst = list.reduce((a, e) => a + (e.gst_amount || 0), 0);
  const byCat = Object.entries(list.reduce((m, e) => ({ ...m, [e.category]: (m[e.category] || 0) + e.amount }), {}))
    .sort((a, b) => b[1] - a[1]);

  return (
    <div>
      <PageHeader eyebrow="Accounts" title="Expenses" subtitle="Rent, salaries, utilities and every other running cost — so profit reflects what the shop really earns.">
        <button className="btn btn-gold" disabled={!list.length} onClick={() => downloadCSV("expenses.csv", ["number", "date", "category", "description", "payee", "reference", "method", "amount", "gst_amount", "total"], list)}><Download size={16} /> Export CSV</button>
        <button className="btn btn-primary" onClick={() => setForm({ open: true, initial: null })} data-testid="new-expense-btn"><Plus size={16} /> Record expense</button>
      </PageHeader>

      <div className="mb-5"><PeriodPicker presets={presets} value={period} onChange={setPeriod} /></div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KPI label="Total spent" value={inr(total)} sub={`${list.length} expense${list.length === 1 ? "" : "s"}`} accent="terra" />
        <KPI label="Before GST" value={inr(base)} sub="Counts against profit" accent="ink" />
        <KPI label="GST paid" value={inr(gst)} sub="Claimable as input credit" accent="gold" />
        <KPI label="Largest category" value={byCat[0]?.[0] || "—"} sub={byCat[0] ? inr(byCat[0][1]) : "No expenses"} accent="ink" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C929B]" />
              <input className="field pl-9" placeholder="Search description, payee, bill no…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="expense-search" />
            </div>
            <select className="field w-auto" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="expense-filter-category">
              <option value="">All categories</option>
              {(s?.expense_categories || []).map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="card overflow-hidden">
            <table className="tbl">
              <thead><tr><th>Date</th><th>Expense</th><th>Paid to</th><th>Method</th><th className="num">Amount</th><th className="num">GST</th><th className="num">Total</th>{isOwner && <th></th>}</tr></thead>
              <tbody>
                {list.map((e) => (
                  <tr key={e.id} data-testid={`expense-row-${e.number}`}>
                    <td className="text-[#5C626A] whitespace-nowrap">{fmtDate(e.date)}</td>
                    <td>
                      <div className="font-medium">{e.category}</div>
                      <div className="text-xs text-[#8C929B]">{e.number}{e.description ? ` · ${e.description}` : ""}{e.reference ? ` · bill ${e.reference}` : ""}</div>
                    </td>
                    <td className="text-[#5C626A]">{e.payee || "—"}</td>
                    <td className="text-[#5C626A]">{e.method}</td>
                    <td className="num font-num">{inr(e.amount)}</td>
                    <td className="num font-num text-[#8C929B]">{inr(e.gst_amount || 0)}</td>
                    <td className="num font-num font-medium">{inr(e.total)}</td>
                    {isOwner && (
                      <td className="num whitespace-nowrap">
                        <button className="p-1.5 text-[#5C626A] hover:text-[#0E1B2E]" title="Edit" onClick={() => setForm({ open: true, initial: e })}><Pencil size={14} /></button>
                        <button className="p-1.5 text-[#A3411D] hover:opacity-70" title="Delete" onClick={() => remove(e)}><Trash2 size={14} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {list.length === 0 && <Empty>No expenses in this period</Empty>}
          </div>
        </div>

        <Section title="By category">
          {byCat.length === 0 ? <Empty>Nothing yet</Empty> : (
            <div className="p-5 space-y-3">
              {byCat.map(([c, v]) => (
                <div key={c}>
                  <div className="flex justify-between text-sm mb-1"><span>{c}</span><span className="font-num">{inr(v)}</span></div>
                  <div className="h-1.5 rounded-full bg-[#F4F0EA] overflow-hidden"><div className="h-full bg-[#A3411D]" style={{ width: `${(v / byCat[0][1]) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <ExpenseForm open={form.open} initial={form.initial} onClose={() => setForm({ open: false, initial: null })} onSaved={load} />
    </div>
  );
}
